import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  Checkbox,
  Radio,
  Cascader,
  Select,
  Button,
  Typography,
  Space,
  Divider,
  Tag,
  theme,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { BaseOptionType } from 'antd/es/cascader';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

// Field configuration types
export type FieldType =
  | 'checkbox-cards' // Status filter style (active/inactive cards)
  | 'radio-cards' // Single-choice card row (e.g. status active/inactive/all)
  | 'multiselect' // Multi-select dropdown
  | 'multiselect-with-tags' // Multi-select with category-style tags and color bars
  | 'cascader'; // Hierarchical location cascader

export interface SelectOption {
  label: string;
  value: string | number;
  color?: string; // For category-style options
}

export interface CascaderOption extends BaseOptionType {
  value: string;
  label: string;
  children?: CascaderOption[];
  isLeaf?: boolean;
}

export interface CheckboxCardOption {
  key: string;
  label: string;
}

export interface BaseFieldConfig {
  key: string;
  label: string;
  type: FieldType;
  /** Localized per-field reset link text (defaults to common:actions.reset) */
  resetText?: string;
}

export interface CheckboxCardsFieldConfig extends BaseFieldConfig {
  type: 'checkbox-cards';
  options: CheckboxCardOption[];
}

export interface RadioCardOption {
  value: string;
  label: string;
}

export interface RadioCardsFieldConfig extends BaseFieldConfig {
  type: 'radio-cards';
  options: RadioCardOption[];
  /** Value the field resets to (e.g. 'all') */
  defaultValue?: string;
}

export interface MultiSelectFieldConfig extends BaseFieldConfig {
  type: 'multiselect';
  options: SelectOption[];
  placeholder?: string;
}

export interface MultiSelectWithTagsFieldConfig extends BaseFieldConfig {
  type: 'multiselect-with-tags';
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  noSelectionText?: string;
  noDataText?: string;
  noSearchResultText?: string;
}

export interface CascaderFieldConfig extends BaseFieldConfig {
  type: 'cascader';
  options: CascaderOption[];
  placeholder?: string;
  loading?: boolean;
  loadingText?: string;
  noDataText?: string;
  noSearchResultText?: string;
}

export type FieldConfig =
  | CheckboxCardsFieldConfig
  | RadioCardsFieldConfig
  | MultiSelectFieldConfig
  | MultiSelectWithTagsFieldConfig
  | CascaderFieldConfig;

/**
 * Valid filter value types - union of all possible filter values
 * @see https://refine.dev/blog/typescript-record-type/
 */
export type FilterValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | number[]
  | (string | number)[]
  | (string | number)[][] // Cascader multiple values
  | CheckboxCardValue // Checkbox cards object
  | Record<string, unknown>;

/**
 * Checkbox card filter value type - object with boolean values
 */
export interface CheckboxCardValue {
  [key: string]: boolean;
}

export interface FilterValues {
  [key: string]: FilterValue;
}

interface GenericFilterPopoverProps<T extends FilterValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (filters: T) => void;
  initialFilters: T;
  children: React.ReactElement;
  fields: FieldConfig[];
  width?: string | number;
  translationNamespace?: string;
  /** Localized apply button text (defaults to common:actions.apply) */
  applyText?: string;
  /** Localized cancel button text (defaults to common:actions.cancel) */
  cancelText?: string;
}

// Empty state component
interface FilterEmptyStateProps {
  isSearching: boolean;
  noDataText: string;
  noSearchResultText: string;
  colorTextTertiary: string;
  fontSizeSM: number;
}

const FilterEmptyState = ({
  isSearching,
  noDataText,
  noSearchResultText,
  colorTextTertiary,
  fontSizeSM,
}: FilterEmptyStateProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '120px',
      padding: '16px',
      color: colorTextTertiary,
      fontSize: fontSizeSM,
    }}
  >
    {isSearching ? noSearchResultText : noDataText}
  </div>
);

const GenericFilterPopover = <T extends FilterValues>({
  open,
  onOpenChange,
  onApply,
  initialFilters,
  children,
  fields,
  width = '400px',
  applyText,
  cancelText,
}: GenericFilterPopoverProps<T>) => {
  const { token } = theme.useToken();
  // Localized fallbacks for every optional text prop (house i18n rule:
  // no hardcoded-English strings, even as defensive defaults).
  const { t } = useTranslation('common');
  const applyLabel = applyText ?? t('actions.apply');
  const cancelLabel = cancelText ?? t('actions.cancel');

  // State management for all field types
  const [filterState, setFilterState] = useState<FilterValues>(initialFilters);
  const [searchValues, setSearchValues] = useState<{ [key: string]: string }>({});

  // Reset state when popover opens with new initial values
  useEffect(() => {
    if (open) {
      setFilterState(initialFilters);
      setSearchValues({});
    }
  }, [open, initialFilters]);

  // Generic update handler
  const updateField = (key: string, value: FilterValue) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
  };

  // Generic reset handler
  const resetField = (key: string, defaultValue: FilterValue) => {
    setFilterState((prev) => ({ ...prev, [key]: defaultValue }));
  };

  // Convert hex to rgba
  const hexToRgba = (hex: string, alpha: number = 0.1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleApply = () => {
    onApply(filterState as T);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setFilterState(initialFilters);
    setSearchValues({});
    onOpenChange(false);
  };

  // Render checkbox cards (status filter style)
  const renderCheckboxCards = (field: CheckboxCardsFieldConfig) => {
    const value = (filterState[field.key] || {}) as CheckboxCardValue;

    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: token.colorFillQuaternary,
          borderRadius: token.borderRadiusLG,
        }}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <Text
            strong
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {field.label}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() =>
              resetField(
                field.key,
                field.options.reduce((acc, opt) => ({ ...acc, [opt.key]: false }), {})
              )
            }
            style={{ padding: 0, height: 'auto', fontSize: '12px' }}
          >
            {field.resetText ?? t('actions.reset')}
          </Button>
        </div>
        <style>
          {`
            .generic-filter-checkbox-card-${field.key} {
              flex: 1;
              padding: 8px 10px;
              cursor: pointer;
              transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            }
            .generic-filter-checkbox-card-${field.key}:hover {
              background-color: ${token.colorPrimaryBg} !important;
              border-color: ${token.colorPrimary} !important;
              transform: translateY(-1px);
            }
          `}
        </style>
        <div className="tw-flex tw-gap-2">
          {field.options.map((option) => (
            <div
              key={option.key}
              className={`generic-filter-checkbox-card-${field.key}`}
              style={{
                backgroundColor: value[option.key]
                  ? token.colorPrimaryBg
                  : token.colorFillQuaternary,
                borderRadius: token.borderRadiusLG,
                border: `1.5px solid ${value[option.key] ? token.colorPrimary : 'transparent'}`,
                boxShadow: value[option.key] ? `0 0 0 3px ${token.colorPrimaryBg}` : 'none',
              }}
              onClick={() => updateField(field.key, { ...value, [option.key]: !value[option.key] })}
            >
              <Checkbox
                checked={value[option.key]}
                onChange={(e) => {
                  e.stopPropagation();
                  updateField(field.key, { ...value, [option.key]: e.target.checked });
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Text strong style={{ fontSize: '12px' }}>
                  {option.label}
                </Text>
              </Checkbox>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render radio cards (single-choice status filter style, e.g. active/inactive/all)
  const renderRadioCards = (field: RadioCardsFieldConfig) => {
    const value = (filterState[field.key] ?? field.defaultValue) as string | undefined;

    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: token.colorFillQuaternary,
          borderRadius: token.borderRadiusLG,
        }}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <Text
            strong
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {field.label}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() => resetField(field.key, field.defaultValue)}
            style={{ padding: 0, height: 'auto', fontSize: '12px' }}
          >
            {field.resetText ?? t('actions.reset')}
          </Button>
        </div>
        <style>
          {`
            .generic-filter-radio-card-${field.key} {
              flex: 1;
              padding: 10px 12px;
              cursor: pointer;
              transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .generic-filter-radio-card-${field.key}:hover {
              background-color: ${token.colorPrimaryBg} !important;
              border-color: ${token.colorPrimary} !important;
              transform: translateY(-1px);
            }
            .generic-filter-radio-card-${field.key} .ant-radio-wrapper {
              margin-right: 0;
            }
          `}
        </style>
        <Radio.Group
          value={value}
          onChange={(e: RadioChangeEvent) => updateField(field.key, e.target.value)}
          style={{ width: '100%' }}
        >
          <div className="tw-flex tw-gap-2">
            {field.options.map((option) => (
              <div
                key={option.value}
                className={`generic-filter-radio-card-${field.key}`}
                style={{
                  backgroundColor:
                    value === option.value ? token.colorPrimaryBg : token.colorBgContainer,
                  borderRadius: radiusTokens.card,
                  border: `1.5px solid ${
                    value === option.value ? token.colorPrimary : token.colorBorder
                  }`,
                }}
                onClick={() => updateField(field.key, option.value)}
              >
                <Radio value={option.value}>
                  <Text strong style={{ fontSize: '13px' }}>
                    {option.label}
                  </Text>
                </Radio>
              </div>
            ))}
          </div>
        </Radio.Group>
      </div>
    );
  };

  // Render simple multi-select
  const renderMultiSelect = (field: MultiSelectFieldConfig) => {
    const value = (filterState[field.key] || []) as Array<string | number>;

    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: token.colorFillQuaternary,
          borderRadius: token.borderRadiusLG,
        }}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <Text
            strong
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {field.label}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() => resetField(field.key, [])}
            style={{ padding: 0, height: 'auto', fontSize: '12px' }}
          >
            {field.resetText ?? t('actions.reset')}
          </Button>
        </div>

        <Select
          mode="multiple"
          placeholder={field.placeholder}
          value={value}
          onChange={(newValue) => updateField(field.key, newValue)}
          style={{ width: '100%' }}
          options={field.options}
          optionFilterProp="label"
          allowClear
        />
      </div>
    );
  };

  // Render multi-select with category-style tags
  const renderMultiSelectWithTags = (field: MultiSelectWithTagsFieldConfig) => {
    const selectedValues = (filterState[field.key] || []) as Array<string | number>;
    const searchValue = searchValues[field.key] || '';

    const handleRemoveTag = (valueToRemove: string | number) => {
      updateField(
        field.key,
        selectedValues.filter((v) => v !== valueToRemove)
      );
    };

    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: token.colorFillQuaternary,
          borderRadius: token.borderRadiusLG,
        }}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <Text
            strong
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {field.label}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() => resetField(field.key, [])}
            style={{ padding: 0, height: 'auto', fontSize: '12px' }}
          >
            {field.resetText ?? t('actions.reset')}
          </Button>
        </div>

        {/* Selected tags display area */}
        <div
          style={{
            marginBottom: '10px',
            padding: '8px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.card,
            height: '60px',
            overflowY: 'auto',
          }}
        >
          {selectedValues.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: token.colorTextTertiary,
              }}
            >
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {field.noSelectionText || t('messages.noItemsSelected')}
              </Text>
            </div>
          ) : (
            <Space size={[4, 4]} wrap>
              {selectedValues.map((val) => {
                const option = field.options.find((o) => o.value === val);
                const color = option?.color || token.colorPrimary;
                return (
                  <Tag
                    key={val}
                    bordered={false}
                    closable
                    onClose={() => handleRemoveTag(val)}
                    style={{
                      margin: 0,
                      padding: '0px 4px',
                      fontSize: '11px',
                      lineHeight: '18px',
                      borderRadius: token.borderRadiusSM,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: hexToRgba(color, 0.1),
                      color: color,
                    }}
                  >
                    {option?.label}
                  </Tag>
                );
              })}
            </Space>
          )}
        </div>

        {/* Select dropdown */}
        <style>
          {`
            .generic-filter-select-${field.key} .ant-select-selection-overflow-item:not(.ant-select-selection-overflow-item-suffix) {
              display: none !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-overflow-item-suffix {
              display: block !important;
            }
            .generic-filter-select-${field.key} .ant-select-selector {
              min-height: 36px !important;
              height: 36px !important;
              display: flex !important;
              align-items: center !important;
              padding: 0 8px !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-wrap {
              height: 100% !important;
              display: flex !important;
              align-items: center !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-overflow {
              height: 100% !important;
              display: flex !important;
              align-items: center !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-overflow-item {
              height: 100% !important;
              display: flex !important;
              align-items: center !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-search {
              height: 100% !important;
              display: flex !important;
              align-items: center !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-search-input {
              height: 100% !important;
            }
            .generic-filter-select-${field.key} .ant-select-selection-placeholder {
              position: absolute !important;
              top: 0 !important;
              bottom: 0 !important;
              inset-inline-start: 11px !important;
              inset-inline-end: 11px !important;
              transform: none !important;
              display: flex !important;
              align-items: center !important;
              line-height: normal !important;
            }
            .generic-filter-dropdown-${field.key} {
              background-color: ${token.colorBgElevated} !important;
              border: 1px solid ${token.colorBorderSecondary} !important;
              border-radius: 11px !important;
              box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
            }
            .generic-filter-dropdown-${field.key} .ant-select-item {
              padding: 0 !important;
              height: 36px !important;
              display: flex !important;
              align-items: center !important;
            }
            .generic-filter-dropdown-${field.key} .ant-select-item .ant-select-item-option-content {
              height: 36px !important;
              display: flex !important;
              align-items: center !important;
            }
          `}
        </style>
        <Select
          mode="multiple"
          placeholder={field.searchPlaceholder || field.placeholder}
          value={selectedValues}
          onChange={(newValue) => updateField(field.key, newValue)}
          onSearch={(value) => setSearchValues((prev) => ({ ...prev, [field.key]: value }))}
          className={`generic-filter-select-${field.key}`}
          classNames={{ popup: { root: `generic-filter-dropdown-${field.key}` } }}
          listHeight={200}
          style={{
            width: '100%',
            borderRadius: radiusTokens.card,
          }}
          options={field.options.map((opt) => ({
            ...opt,
            label: (
              <div
                className="tw-flex tw-items-center tw-gap-2"
                style={{
                  padding: '4px 8px',
                }}
              >
                {opt.color && (
                  <div
                    style={{
                      width: '3px',
                      height: token.sizeXS,
                      backgroundColor: opt.color,
                      borderRadius: token.borderRadiusSM,
                    }}
                  />
                )}
                <Text style={{ fontSize: '12px' }}>{opt.label}</Text>
              </div>
            ),
          }))}
          notFoundContent={
            <FilterEmptyState
              isSearching={!!searchValue}
              noDataText={field.noDataText || t('messages.noData')}
              noSearchResultText={field.noSearchResultText || t('messages.noSearchResults')}
              colorTextTertiary={token.colorTextTertiary}
              fontSizeSM={token.fontSizeSM}
            />
          }
          maxTagCount={0}
          maxTagPlaceholder={() => null}
          removeIcon={null}
          clearIcon={null}
          menuItemSelectedIcon={null}
          allowClear={false}
          variant="outlined"
        />
      </div>
    );
  };

  // Render cascader
  const renderCascader = (field: CascaderFieldConfig) => {
    const value = (filterState[field.key] || []) as string[][];
    const searchValue = searchValues[field.key] || '';

    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: token.colorFillQuaternary,
          borderRadius: token.borderRadiusLG,
        }}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <Text
            strong
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {field.label}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() => resetField(field.key, [])}
            style={{ padding: 0, height: 'auto', fontSize: '12px' }}
          >
            {field.resetText ?? t('actions.reset')}
          </Button>
        </div>

        <Cascader<CascaderOption>
          value={value}
          options={field.options}
          onChange={(newValue) => updateField(field.key, newValue)}
          multiple
          style={{ width: '100%', borderRadius: radiusTokens.card }}
          placeholder={field.loading ? field.loadingText : field.placeholder}
          disabled={field.loading}
          expandTrigger="hover"
          showCheckedStrategy="SHOW_PARENT"
          changeOnSelect
          allowClear={false}
          displayRender={(labels) => labels.join(' > ')}
          notFoundContent={
            <FilterEmptyState
              isSearching={!!searchValue}
              noDataText={field.noDataText || t('messages.noData')}
              noSearchResultText={field.noSearchResultText || t('messages.noSearchResults')}
              colorTextTertiary={token.colorTextTertiary}
              fontSizeSM={token.fontSizeSM}
            />
          }
          onSearch={(value) => setSearchValues((prev) => ({ ...prev, [field.key]: value }))}
          showSearch={{
            filter: (inputValue, path) => {
              return path.some((option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              );
            },
            render: (_inputValue, path) => {
              return path.map((opt) => opt.label).join(' > ');
            },
            limit: 100,
            matchInputWidth: true,
          }}
          popupRender={(menus) => (
            <div className={`generic-filter-cascader-${field.key}`}>
              <style>
                {`
                  .generic-filter-cascader-${field.key} {
                    border-radius: 11px !important;
                    background-color: ${token.colorBgElevated} !important;
                    border: 1px solid ${token.colorBorderSecondary} !important;
                    box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-menu {
                    border-radius: 11px !important;
                    background-color: ${token.colorBgElevated} !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-menu-item {
                    height: 36px !important;
                    padding: 0 8px !important;
                    line-height: 36px !important;
                    font-size: 12px !important;
                    display: flex !important;
                    align-items: center !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox {
                    margin-right: 10px !important;
                    margin-left: 2px !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox-inner {
                    border-radius: 2px !important;
                    width: 16px !important;
                    height: 16px !important;
                    border-width: 1px !important;
                    border-style: solid !important;
                    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1) !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox:not(.ant-cascader-checkbox-checked):not(.ant-cascader-checkbox-indeterminate) .ant-cascader-checkbox-inner {
                    background-color: ${token.colorBgContainer} !important;
                    border-color: ${token.colorBorder} !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox-checked .ant-cascader-checkbox-inner {
                    background-color: ${token.colorPrimary} !important;
                    border-color: ${token.colorPrimary} !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox-checked .ant-cascader-checkbox-inner::after {
                    position: absolute !important;
                    top: 50% !important;
                    inset-inline-start: 21.5% !important;
                    display: table !important;
                    width: 5.71428571px !important;
                    height: 9.14285714px !important;
                    border: 2px solid #fff !important;
                    border-top: 0 !important;
                    border-inline-start: 0 !important;
                    transform: rotate(45deg) scale(1) translate(-50%, -50%) !important;
                    opacity: 1 !important;
                    content: ' ' !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox-indeterminate .ant-cascader-checkbox-inner {
                    background-color: ${token.colorPrimary} !important;
                    border-color: ${token.colorPrimary} !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox-indeterminate .ant-cascader-checkbox-inner::after {
                    position: absolute !important;
                    top: 50% !important;
                    left: 50% !important;
                    width: 8px !important;
                    height: 8px !important;
                    background-color: #fff !important;
                    border: 0 !important;
                    transform: translate(-50%, -50%) scale(1) !important;
                    opacity: 1 !important;
                    content: ' ' !important;
                  }
                  .generic-filter-cascader-${field.key} .ant-cascader-checkbox:hover .ant-cascader-checkbox-inner {
                    border-color: ${token.colorPrimary} !important;
                  }
                `}
              </style>
              {menus}
            </div>
          )}
        />
        <style>
          {`
            .ant-cascader .ant-select-selector {
              border-radius: 8px !important;
              min-height: 36px !important;
              max-height: 60px !important;
              height: auto !important;
              display: flex !important;
              align-items: center !important;
              padding: 5px 8px 1px 8px !important;
              overflow-y: auto !important;
            }
            .ant-cascader .ant-select-selection-overflow {
              display: flex !important;
              align-items: center !important;
              flex-wrap: wrap !important;
            }
            .ant-cascader .ant-select-selection-overflow-item {
              margin-right: 4px !important;
              margin-bottom: 4px !important;
              display: flex !important;
              align-items: center !important;
            }
            .ant-cascader .ant-select-selection-item {
              height: auto !important;
              line-height: 18px !important;
              padding: 0px 4px !important;
              font-size: 11px !important;
              white-space: normal !important;
              word-break: break-word !important;
              max-width: 100% !important;
              background-color: ${token.colorPrimaryBg} !important;
              border: 1px solid ${token.colorPrimaryBorder} !important;
              border-radius: ${token.borderRadiusSM}px !important;
              color: ${token.colorPrimary} !important;
              margin: 0 !important;
            }
            .ant-cascader .ant-select-selection-item-content {
              white-space: normal !important;
              word-break: break-word !important;
              overflow: visible !important;
              text-overflow: unset !important;
            }
            .ant-cascader .ant-select-selection-item-remove {
              color: ${token.colorPrimary} !important;
              font-size: 10px !important;
              margin-left: 2px !important;
            }
            .ant-cascader .ant-select-selection-item-remove:hover {
              color: ${token.colorPrimaryHover} !important;
            }
            .ant-cascader .ant-select-selection-search {
              height: 100% !important;
              display: flex !important;
              align-items: center !important;
            }
            .ant-cascader .ant-select-selection-search-input {
              height: 100% !important;
            }
            .ant-cascader .ant-select-selection-placeholder {
              position: absolute !important;
              top: 0 !important;
              bottom: 0 !important;
              inset-inline-start: 11px !important;
              inset-inline-end: 11px !important;
              transform: none !important;
              display: flex !important;
              align-items: center !important;
              line-height: normal !important;
              font-size: 12px !important;
            }
          `}
        </style>
      </div>
    );
  };

  // Render field based on type
  const renderField = (field: FieldConfig) => {
    switch (field.type) {
      case 'checkbox-cards':
        return renderCheckboxCards(field);
      case 'radio-cards':
        return renderRadioCards(field);
      case 'multiselect':
        return renderMultiSelect(field);
      case 'multiselect-with-tags':
        return renderMultiSelectWithTags(field);
      case 'cascader':
        return renderCascader(field);
      default:
        return null;
    }
  };

  const content = (
    <div style={{ padding: '12px' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {fields.map((field) => (
          <div key={field.key}>{renderField(field)}</div>
        ))}

        <Divider style={{ margin: 0 }} />

        {/* Action Buttons */}
        <div className="tw-flex tw-gap-2 tw-justify-end">
          <Button onClick={handleCancel} style={{ height: `${controlHeightTokens.controlHeight}px`, fontSize: token.fontSizeSM }}>
            {cancelLabel}
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            style={{ height: `${controlHeightTokens.controlHeight}px`, fontSize: token.fontSizeSM }}
          >
            {applyLabel}
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomLeft"
      arrow={false}
      overlayStyle={{
        width,
      }}
      styles={{
        body: {
          padding: 0,
          backgroundColor: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          boxShadow:
            '0 12px 28px 0 rgba(0, 0, 0, 0.12), 0 6px 12px -6px rgba(0, 0, 0, 0.16), 0 18px 56px 16px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {children}
    </Popover>
  );
};

export default GenericFilterPopover;
